// services/childcare/utils.js
import axios from "axios";
import { jwtDecode } from "jwt-decode";

const API_BASE_URL =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:5000/api";

// Create axios instances
export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
  timeout: 30000,
});

export const uploadApi = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "multipart/form-data" },
  timeout: 60000,
});

// ==================== TOKEN MANAGEMENT ====================

// Get token from storage
export const getToken = () => {
  return localStorage.getItem("token") || sessionStorage.getItem("token");
};

// Set token in storage
export const setToken = (token, remember = false) => {
  if (remember) {
    localStorage.setItem("token", token);
  } else {
    sessionStorage.setItem("token", token);
  }
};

// Remove token
export const removeToken = () => {
  localStorage.removeItem("token");
  sessionStorage.removeItem("token");
};

// Decode JWT token to get payload
export const decodeToken = () => {
  const token = getToken();
  if (!token) return null;

  try {
    const decoded = jwtDecode(token);
    return decoded;
  } catch (error) {
    console.error("Error decoding token:", error);
    return null;
  }
};

// ==================== USER SESSION HELPERS ====================

// Get stored user from localStorage/sessionStorage
export const getStoredUser = () => {
  const user = localStorage.getItem("user") || sessionStorage.getItem("user");
  return user ? JSON.parse(user) : null;
};

// Get user from JWT token (more reliable)
export const getUserFromToken = () => {
  const decoded = decodeToken();
  if (!decoded) return null;

  return {
    id: decoded.id || decoded.userId,
    email: decoded.email,
    // ✅ Support both role and user_type from token
    role: decoded.role || decoded.user_type,
    user_type: decoded.user_type || decoded.role,
    name: decoded.name || decoded.fname,
    exp: decoded.exp,
  };
};

// Get current user (combines stored user and token data)
export const fetchCurrentUser = () => {
  // First try to get from token (most reliable)
  const tokenUser = getUserFromToken();
  if (tokenUser && tokenUser.id) {
    return tokenUser;
  }

  // Fallback to stored user
  return getStoredUser();
};

// ✅ Get user role (supports both 'role' and 'user_type')
export const getUserRole = () => {
  const user = fetchCurrentUser();
  if (!user) return null;

  // Priority: role > user_type
  return user.role || user.user_type || null;
};

// ✅ Get user type (alias for getUserRole for backward compatibility)
export const getUserType = () => {
  return getUserRole();
};

// Check if user is logged in
export const isLoggedIn = () => {
  const token = getToken();
  return !!token;
};

// ==================== ROLE CHECKING FUNCTIONS ====================

// ✅ Generic role checker (supports both role and user_type)
export const hasRole = (allowedRoles) => {
  const userRole = getUserRole();
  if (!userRole) return false;

  // Convert to array if single string
  const roles = Array.isArray(allowedRoles) ? allowedRoles : [allowedRoles];

  return roles.includes(userRole);
};

// ✅ Check if user has any of the allowed roles (alias for hasRole)
export const hasAnyRole = (allowedRoles) => hasRole(allowedRoles);

// ✅ Check if user has all specified roles (for multiple role requirements)
export const hasAllRoles = (requiredRoles) => {
  const userRole = getUserRole();
  if (!userRole) return false;

  return requiredRoles.every((role) => userRole === role);
};

// Specific role checkers
export const isParent = () => hasRole(["parent"]);
export const isChildcareAdmin = () => hasRole(["childcare_admin"]);
export const isChildcareCoAdmin = () => hasRole(["childcare_co_admin"]);
export const isChildcareStaff = () => hasRole(["childcare_caregiver", "childcare_tutor"]);
export const isChildcareUser = () =>
  hasRole(["childcare_admin", "childcare_co_admin", "childcare_caregiver", "childcare_tutor"]);
export const isTutor = () => hasRole(["tutor"]);
export const isPartner = () => hasRole(["partner"]);
export const isAdmin = () => hasRole(["admin"]);

// Check if user has specific permission (for more granular permissions)
export const hasPermission = (permission) => {
  const userRole = getUserRole();
  if (!userRole) return false;

  const permissions = {
    parent: [
      "view_facilities",
      "enroll_children",
      "write_reviews",
      "view_enrollments",
    ],
    childcare_admin: [
      "all",
      "manage_facility",
      "manage_staff",
      "manage_children",
      "view_reports",
    ],
    childcare_co_admin: [
      "view_children",
      "view_attendance",
      "view_reports",
      "manage_attendance",
    ],
    childcare_staff: [
      "view_children",
      "mark_attendance",
      "view_assigned_children",
    ],
    tutor: ["manage_classes", "view_students", "mark_attendance"],
    partner: ["manage_facility", "view_reports", "manage_staff"],
    admin: ["all"],
  };

  const userPermissions = permissions[userRole] || [];
  return (
    userPermissions.includes("all") || userPermissions.includes(permission)
  );
};

// ==================== FACILITY ACCESS HELPERS ====================

export const getFacilityId = () => {
  return (
    localStorage.getItem("facilityId") || sessionStorage.getItem("facilityId")
  );
};

export const hasChildcareAccess = () => {
  return isChildcareUser();
};

export const isChildcareLoggedIn = () => {
  return isLoggedIn() && isChildcareUser();
};

// ==================== LOGOUT ====================

export const logout = () => {
  removeToken();
  localStorage.removeItem("user");
  sessionStorage.removeItem("user");
  localStorage.removeItem("facilityId");
  sessionStorage.removeItem("facilityId");
};

export const childcareLogout = () => {
  logout();
  window.location.href = "/childcare/login";
};

export const parentLogout = () => {
  logout();
  window.location.href = "/parentlogin";
};

export const generalLogout = () => {
  logout();
  window.location.href = "/";
};

// ==================== AXIOS INTERCEPTORS ====================

// Attach token automatically
api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

uploadApi.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ✅ Define public routes that should NOT redirect on 401
// eslint-disable-next-line no-unused-vars
const isPublicRoute = (url) => {
  if (!url) return false;

  const publicPaths = [
    "/childcare/facilities",
    "/childcare/filter-options",
    "/childcare/check-email",
    "/childcare/check-phone",
    "/childcare/send-email-otp",
    "/childcare/verify-email-otp",
    "/childcare/send-phone-otp",
    "/childcare/verify-phone-otp",
    "/childcare/login",
    "/childcare/forgot-password",
    "/childcare/verify-otp",
    "/childcare/reset-password",
    "/childcare/registration",
    "/childcare/invitations/verify",
    "/facilities", // Public facility endpoints
    "/facilities/",
  ];

  return publicPaths.some((path) => url.includes(path));
};

// Handle 401 responses for api
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      console.warn("API 401 on", error.config?.url);
    }
    return Promise.reject(error);
  },
);

// Handle 401 responses for uploadApi
uploadApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      console.warn("Upload API 401 on", error.config?.url);
    }
    return Promise.reject(error);
  },
);

// Phone numbers are stored and sent exactly as the user entered them — no
// automatic "+" prefix. Kept as a pass-through (rather than removed) so
// existing call sites don't need to change.
export const formatPhoneNumber = (phone) => phone;

// ==================== USER STORAGE HELPERS ====================

// Save user to storage after login
export const saveUser = (userData, remember = false) => {
  const storage = remember ? localStorage : sessionStorage;
  storage.setItem("user", JSON.stringify(userData));

  // If user has facilityId, store it separately
  if (userData.facilityId) {
    storage.setItem("facilityId", userData.facilityId);
  }
};

// Update user in storage
export const updateUser = (updates) => {
  const currentUser = getStoredUser();
  if (currentUser) {
    const updatedUser = { ...currentUser, ...updates };
    const storage = localStorage.getItem("user")
      ? localStorage
      : sessionStorage;
    storage.setItem("user", JSON.stringify(updatedUser));
  }
};

// Get user display name
export const getUserDisplayName = () => {
  const user = fetchCurrentUser();
  if (!user) return "Guest";
  return user.name || user.fname || user.email || "User";
};

// Get user role display name
export const getRoleDisplayName = () => {
  const role = getUserRole();
  const roleNames = {
    parent: "Parent",
    childcare_admin: "Childcare Administrator",
    childcare_co_admin: "Childcare Co-Administrator",
    childcare_staff: "Childcare Staff",
    tutor: "Tutor",
    partner: "Partner",
    admin: "Administrator",
  };
  return roleNames[role] || "User";
};
